import { SettingsNavParams } from '@euler/app/flows/settings/screens';
import { Button } from '@euler/components/Button';
import { useModalWrapperSize } from '@euler/components/ModalWrapper';
import { FormGroup, FormInput } from '@euler/components/form';
import { useAppLoading } from '@euler/components/loading/Provider';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBackButtonHandler } from '@euler/functions/useBackButtonHandler';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { PredefinedVerifyCodeScene } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { isMobilePhone } from '@euler/utils/validators';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { nanoid } from 'nanoid/non-secure';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView } from 'react-native';
import Toast from 'react-native-root-toast';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';
import { BackButton } from '../components/BackButton';
import { CustomPageHeader } from '../components/CustomPageHeader';

const kCodeLength = 6;

export const PhoneScreen = wrapNavigatorScreen(
  ({
    title,
    subTitle,
    onDone,
  }: {
    title: string;
    subTitle?: string;
    onDone: (params: {
      mobile: string;
      ticket: string;
      verifyCode: string;
    }) => any;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const onGoBack = useBackButtonHandler();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation<StackNavigationProp<SettingsNavParams>>();
    const loading = useAppLoading();
    const contentContainerStyle = useScreenContentContainerStyle();
    const [mobile, setMobile] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const nonce = useRef(nanoid()).current;
    const [ticket, setTicket] = useState<string>();
    const { smsService } = useServiceFactory();

    useModalWrapperSize({ width: rpx(375), height: rpx(500) });

    useEffect(() => {
      smsService
        .requestVerifyCodeTicket(nonce)
        .then(result => {
          setTicket(result);
        })
        .catch(onErrorIgnore);
    }, [nonce, smsService]);

    const onSendCode = useCallback(
      async (
        callback?: (
          error: Error | null | undefined,
          result?: {
            ticket: string;
            length: number;
            ttl: number;
          },
        ) => void,
      ) => {
        if (!ticket) return;

        loading.show();
        setIsLoading(true);

        try {
          const ttl = await smsService.sendVerifyCode(
            ticket,
            mobile,
            PredefinedVerifyCodeScene.Login,
            kCodeLength,
          );
          setIsLoading(false);
          loading.hide();
          Toast.show('验证码已发送', {
            duration: 300,
          });
          if (callback) {
            callback(null, { ticket, length: kCodeLength, ttl });
          } else {
            navigation.push('_verifyCode', {
              mobile,
              codeInfo: {
                ticket,
                length: kCodeLength,
                ttl,
              },
              onSendCode,
              onDone,
            });
          }
        } catch (e) {
          setIsLoading(false);
          loading.hide();
          Toast.show('发送失败: ' + (e as Error).message, {
            duration: 3000,
          });
          if (callback) {
            callback(e as Error);
          }
        }
      },
      [ticket, loading, smsService, mobile, navigation, onDone],
    );

    const onNext = useCallback(() => {
      if (isLoading) return;
      if (!ticket) {
        Toast.show('发送失败: E_TICKET', {
          duration: 1500,
        });
        return;
      }
      onSendCode().catch(onErrorIgnore);
    }, [isLoading, onSendCode, ticket]);

    return (
      <>
        <ScrollView
          contentContainerStyle={[
            {
              paddingTop: insets.top + rpx(80),
              paddingLeft: rpx(20),
              paddingRight: rpx(20),
              paddingBottom: rpx(20),
            },
            contentContainerStyle,
          ]}
        >
          <CustomPageHeader title={title} subTitle={subTitle} />

          <FormGroup
            css={`
              margin-top: ${rpx(32)}px;
            `}
          >
            <FormInput
              placeholder="手机号"
              value={mobile}
              onChange={setMobile}
              keyboardType="number-pad"
              returnKeyType="next"
              returnKeyLabel="继续"
              autoFocus
              css={`
                font-family: ${FontFamily.NotoSans.Medium};
                font-size: ${rpx(16)}px;
                letter-spacing: 0.5px;
              `}
            />
          </FormGroup>

          <Button
            title="获取验证码"
            disabled={!isMobilePhone(mobile)}
            onPress={onNext}
            textStyle={{ color: '#fff', fontSize: rpx(16) }}
            css={`
              background-color: ${theme.colors.primary};
            `}
          />
        </ScrollView>
        <BackButton
          onPress={onGoBack}
          color={'#333'}
          css={`
            position: absolute;
            left: ${rpx(20)}px;
            top: ${insets.top + rpx(15)}px;
          `}
        />
      </>
    );
  },
  { headerShown: false },
);
